import React from 'react';
import { Form, Input, Select, DatePicker, Switch, InputNumber, Button, Space } from 'antd';
import { DebtTypeOptions, toSelectOptions, labelOfCollectionStatus } from '../../../../constants/debtEnums';
import { amountFormatter, amountParser } from '../../../../utils/numberFormat';

const BasicInfoStep = ({ value, onChange, totalAmount, onNext, config }) => {
  const [form] = Form.useForm();
  const showCollectionStatus = String(value?.status || '').toUpperCase() !== 'DRAFT';

  // 监听金额相关字段，前端自动统计“总涉及金额”（本金+违约金+其他）
  const principal = Form.useWatch('principal_amount', form);
  const penalty = Form.useWatch('penalty_amount', form);
  const other = Form.useWatch('other_amount', form);
  const toNum = (v) => {
    const n = Number(amountParser(v ?? 0));
    return Number.isFinite(n) ? n : 0;
  };
  const totalInvolved = toNum(principal) + toNum(penalty) + toNum(other);

  // 保证返回到本步骤时表单值与父组件保持同步（initialValues只在首次渲染生效）
  React.useEffect(() => {
    if (value) {
      form.setFieldsValue(value);
    }
  }, [value, form]);

  const handleValuesChange = (_, allValues) => {
    const p = toNum(allValues.principal_amount ?? principal);
    const pe = toNum(allValues.penalty_amount ?? penalty);
    const o = toNum(allValues.other_amount ?? other);
    const t = p + pe + o;
    onChange(prev => ({ ...prev, ...allValues, total_amount: t }));
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      onNext();
    } catch (e) {}
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={value}
      onValuesChange={handleValuesChange}
    >
      <Form.Item label="债权名称" name="title" rules={[
        { required: true, message: '请填写债权名称' },
        { max: 15, message: '最多15个字符' },
      ]}> 
        <Input placeholder="请输入债权名称（最多15个字符）" maxLength={15} />
      </Form.Item>

      <Form.Item label="债权类型" name="debt_type" rules={[{ required: true, message: '请选择债权类型' }]}> 
        <Select options={toSelectOptions(config?.debt_type || DebtTypeOptions)} placeholder="请选择" />
      </Form.Item>

      <Form.Item label="金额信息" required>
        <Space wrap>
          <Space direction="vertical" size={4}>
            <span>本金</span>
            <Space>
              <Form.Item name="principal_amount" noStyle rules={[
                { required: true, message: '请输入本金金额' },
                { validator: (_, v) => {
                  const num = Number(amountParser(v));
                  return num > 0 ? Promise.resolve() : Promise.reject(new Error('必须大于0'));
                } }
              ]}>
                <InputNumber min={0} precision={0} step={1} style={{ width: 200 }} formatter={amountFormatter} parser={amountParser} />
              </Form.Item>
              <span>人民币</span>
            </Space>
          </Space>

          <Space direction="vertical" size={4}>
            <span>利息</span>
            <Space>
              <Form.Item name="interest_amount" noStyle rules={[
                { validator: (_, v) => (Number(v ?? 0) >= 0 ? Promise.resolve() : Promise.reject(new Error('不得小于0'))) }
              ]}>
                <InputNumber min={0} precision={2} step={0.01} style={{ width: 200 }} />
              </Form.Item>
              <span>%/月</span>
            </Space>
          </Space>

          <Space direction="vertical" size={4}>
            <span>违约金</span>
            <Space>
              <Form.Item name="penalty_amount" noStyle rules={[
                { validator: (_, v) => {
                  const num = Number(amountParser(v ?? 0));
                  return num >= 0 ? Promise.resolve() : Promise.reject(new Error('不得小于0'));
                } }
              ]}>
                <InputNumber min={0} precision={0} step={1} style={{ width: 200 }} formatter={amountFormatter} parser={amountParser} />
              </Form.Item>
              <span>人民币</span>
            </Space>
          </Space>

          <Space direction="vertical" size={4}>
            <span>其他</span>
            <Space>
              <Form.Item name="other_amount" noStyle rules={[
                { validator: (_, v) => {
                  const num = Number(amountParser(v ?? 0));
                  return num >= 0 ? Promise.resolve() : Promise.reject(new Error('不得小于0'));
                } }
              ]}>
                <InputNumber min={0} precision={0} step={1} style={{ width: 200 }} formatter={amountFormatter} parser={amountParser} />
              </Form.Item>
              <span>人民币</span>
            </Space>
          </Space>

          <Space direction="vertical" size={4}>
            <span>总涉及金额</span>
            <Space>
              <Form.Item name="total_amount" noStyle>
                <InputNumber value={Number(totalInvolved)} readOnly precision={0} style={{ width: 200 }} formatter={amountFormatter} parser={amountParser} />
              </Form.Item>
              <span>人民币</span>
            </Space>
          </Space>
        </Space>
      </Form.Item>

      <Form.Item label="债权发生日" name="debt_occurrence_date" rules={[{ required: true, message: '请选择债权发生日' }]}> 
        <DatePicker style={{ width: 240 }} />
      </Form.Item>
      {showCollectionStatus && (
        <Form.Item label="催收状态">
          <Input value={labelOfCollectionStatus(value?.collection_status)} readOnly />
        </Form.Item>
      )}

      <Form.Item label="是否原始债权人" name="is_original_creditor" valuePropName="checked"> 
        <Switch />
      </Form.Item>
      <Form.Item label="是否匿名展示" name="is_anonymous" valuePropName="checked"> 
        <Switch />
      </Form.Item>

      <Form.Item label="债权说明" name="description" rules={[{
        validator: (_, value) => {
          const s = String(value || '').trim();
          const count = s.length;
          if (!s) return Promise.resolve();
          if (count < 10) return Promise.reject(new Error('至少10个字符'));
          if (count > 500) return Promise.reject(new Error('最多500个字符'));
          return Promise.resolve();
        }
      }]} > 
        <Input.TextArea rows={4} placeholder="请填写10-500字说明" maxLength={500} />
      </Form.Item>

      <Form.Item>
        <Button type="primary" onClick={handleNext}>下一步</Button>
      </Form.Item>
    </Form>
  );
};

export default BasicInfoStep;
